"use client";

import ConsultationModal from "@/component/common/ConsultationModal";
import { CALC_UNITS, FREQUENCIES } from "@/data/calculator-data";
import { useContent } from "@/hooks/useContent";
import { useCalculator } from "@/lib/use-calculator";
import type { Bilingual, Locale } from "@/types/calculator";
import { useLocale } from "next-intl";
import { useMemo, useState } from "react";
import CalcControls from "./CalcControls";
import CalcHeader from "./CalcHeader";
import CalcResults from "./CalcResults";
import CalcSchedule from "./CalcSchedule";

interface CalcPlan { id: string; name: Bilingual; years: number; downPayment: number; note?: Bilingual }
interface CalcUnit { id: string; name: Bilingual; price: number }
interface CalcContent { plans: CalcPlan[]; units?: CalcUnit[] }

/** Composes the Calculator page; owns the input state + booking modal. */
export default function CalculatorParent() {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { data: calc } = useContent<CalcContent>("calculator");
  const plans = calc?.plans ?? [];
  // Units + base prices are panel-managed (fallback to the built-in samples).
  const units: CalcUnit[] = useMemo(
    () => (calc?.units?.length ? calc.units : CALC_UNITS.map((u) => ({ id: u.key, name: u.name, price: u.priceEGP }))),
    [calc],
  );

  const [unitKey, setUnitKey] = useState<string | null>(null);
  const [downPct, setDownPct] = useState(15);
  const [years, setYears] = useState(7);
  const [freq, setFreq] = useState("monthly");
  const [modal, setModal] = useState(false);

  const unit = units.find((u) => u.id === unitKey) ?? units[0];
  const perYear = FREQUENCIES.find((f) => f.key === freq)?.perYear ?? 12;
  const result = useCalculator(unit?.price ?? 0, downPct, years, perYear);
  const planSub = ar ? `${downPct}% مقدم · ${years} سنوات` : `${downPct}% down · ${years} years`;

  if (!unit) {
    return (
      <div className="bg-white text-ash dark:bg-ash dark:text-white">
        <CalcHeader />
        <p className="mx-auto max-w-360 px-6 py-24 text-center text-sm text-ash/50 lg:px-10 dark:text-white/50">
          {ar ? "لا توجد وحدات في الحاسبة بعد." : "No calculator units yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white text-ash dark:bg-ash dark:text-white">
      <CalcHeader />

      <section className="mx-auto grid max-w-360 items-start gap-8 px-6 py-12 lg:grid-cols-[45fr_55fr] lg:px-10">
        <CalcControls
          units={units}
          unitKey={unit.id} setUnitKey={setUnitKey} price={unit.price}
          downPct={downPct} setDownPct={setDownPct}
          years={years} setYears={setYears}
          freq={freq} setFreq={setFreq}
        />
        <div>
          <CalcResults result={result} downPct={downPct} freqKey={freq} onBook={() => setModal(true)} />
          <CalcSchedule schedule={result.schedule} />
        </div>
      </section>

      {plans.length > 0 && (
        <section className="mx-auto max-w-360 border-t border-black/10 px-6 py-8 lg:px-10 dark:border-white/10">
          <span className="text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "خطط السداد المتاحة" : "Available payment plans"}</span>
          <div className="mt-4 flex flex-wrap gap-3">
            {plans.map((pl) => {
              const active = pl.years === years && pl.downPayment === downPct;
              return (
                <button
                  key={pl.id}
                  onClick={() => { setYears(pl.years); setDownPct(pl.downPayment); }}
                  className={`border px-4 py-3 text-start transition-colors ${active ? "border-accent bg-accent/5" : "border-black/15 hover:border-accent dark:border-white/15"}`}
                >
                  <div className="text-[12px] font-semibold text-ash dark:text-white">{pl.name[locale]}</div>
                  <div className="mt-0.5 text-[11px] text-ash/55 dark:text-white/55">
                    {ar ? `${pl.downPayment}٪ مقدم · ${pl.years} سنوات` : `${pl.downPayment}% down · ${pl.years} years`}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <ConsultationModal open={modal} onClose={() => setModal(false)} title={unit.name[locale]} subtitle={planSub} />
    </div>
  );
}
